import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, ArrowLeft, UtensilsCrossed, Pencil, Trash2, Power, 
  Search, ChefHat, Tag, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';

export default function RestaurantMenu({ user }) {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  
  const [restaurant, setRestaurant] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [itemFormData, setItemFormData] = useState({
    name: '',
    category_id: '',
    price: '',
    description: ''
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    display_order: '0'
  });

  const canManage = ['super_admin', 'hotel_admin', 'manager'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, [restaurantId]);

  const loadData = async () => {
    try {
      const [restaurantRes, menuRes, catRes] = await Promise.all([
        api.get(`/restaurants/${restaurantId}`),
        api.get(`/menu?restaurant_id=${restaurantId}`),
        api.get(`/menu-categories?restaurant_id=${restaurantId}`)
      ]);
      setRestaurant(restaurantRes.data);
      setItems(menuRes.data);
      setCategories(catRes.data.filter(c => c.is_active !== false));
    } catch (error) {
      toast.error('Failed to load menu data');
      if (error.response?.status === 404) {
        navigate('/admin/restaurants');
      }
    } finally {
      setLoading(false);
    }
  };

  // Item functions
  const resetItemForm = () => {
    setEditingItem(null);
    setItemFormData({ name: '', category_id: '', price: '', description: '' });
  };

  const openAddItemDialog = () => {
    resetItemForm();
    setItemDialogOpen(true);
  };

  const openEditItemDialog = (item) => {
    setEditingItem(item);
    setItemFormData({
      name: item.name,
      category_id: item.category_id || '',
      price: item.price.toString(),
      description: item.description || ''
    });
    setItemDialogOpen(true);
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    if (!itemFormData.name.trim() || !itemFormData.price) {
      toast.error('Name and price are required');
      return;
    }

    const payload = {
      name: itemFormData.name.trim(),
      restaurant_id: restaurantId,
      category_id: itemFormData.category_id || null,
      price: parseFloat(itemFormData.price),
      description: itemFormData.description.trim() || null
    };

    try {
      if (editingItem) {
        await api.put(`/menu/${editingItem.id}`, payload);
        toast.success('Menu item updated');
      } else {
        await api.post('/menu', payload);
        toast.success('Menu item created');
      }
      setItemDialogOpen(false);
      resetItemForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save menu item'));
    }
  };

  const handleItemDelete = async (id) => {
    if (!confirm('Delete this menu item?')) return;
    try {
      await api.delete(`/menu/${id}`);
      toast.success('Menu item deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleToggleItem = async (item) => {
    try {
      await api.patch(`/menu/${item.id}/toggle`);
      toast.success(`Item ${item.is_available ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to toggle item');
    }
  };

  // Category functions
  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', description: '', display_order: '0' });
  };

  const openAddCategoryDialog = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order?.toString() || '0'
    });
    setCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryFormData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    const payload = {
      name: categoryFormData.name.trim(),
      restaurant_id: restaurantId,
      description: categoryFormData.description.trim() || null,
      display_order: parseInt(categoryFormData.display_order) || 0
    };

    try {
      if (editingCategory) {
        await api.put(`/menu-categories/${editingCategory.id}`, payload);
        toast.success('Category updated');
      } else {
        await api.post('/menu-categories', payload);
        toast.success('Category created');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    }
  };

  const handleCategoryDelete = async (id) => {
    const itemsInCategory = items.filter(i => i.category_id === id);
    if (itemsInCategory.length > 0) {
      toast.error(`Cannot delete: ${itemsInCategory.length} items in this category`);
      return;
    }
    if (!confirm('Delete this category?')) return;
    try {
      await api.delete(`/menu-categories/${id}`);
      toast.success('Category deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Filtered items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="menu-loading">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="restaurant-menu">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/restaurants')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-800">{restaurant?.name}</h1>
            <Badge variant="secondary">
              <ChefHat className="h-3 w-3 mr-1" />
              {restaurant?.cuisine_type || 'Restaurant'}
            </Badge>
          </div>
          <p className="text-gray-500">Menu Management</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="items" className="space-y-4">
        <TabsList>
          <TabsTrigger value="items" data-testid="items-tab">
            <UtensilsCrossed className="h-4 w-4 mr-2" />
            Menu Items ({items.length})
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="categories-tab">
            <Tag className="h-4 w-4 mr-2" />
            Categories ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* Menu Items Tab */}
        <TabsContent value="items" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-3 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-items"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canManage && (
              <Button onClick={openAddItemDialog} className="bg-primary hover:bg-primary/90" data-testid="add-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            )}
          </div>

          {filteredItems.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <UtensilsCrossed className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No Menu Items</h3>
                <p className="text-gray-400 mb-4">Add your first menu item to this restaurant</p>
                {canManage && (
                  <Button onClick={openAddItemDialog} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Menu Item
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <Card key={item.id} className={`${!item.is_available ? 'opacity-60' : ''}`} data-testid={`menu-item-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-800">{item.name}</h3>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {item.category || 'Uncategorized'}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary flex items-center">
                          <IndianRupee className="h-4 w-4" />
                          {item.price}
                        </p>
                        <Badge variant={item.is_available ? "default" : "secondary"} className="text-xs">
                          {item.is_available ? 'Available' : 'Unavailable'}
                        </Badge>
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{item.description}</p>
                    )}
                    {canManage && (
                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="ghost" size="sm" onClick={() => openEditItemDialog(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleItem(item)}>
                          <Power className={`h-4 w-4 ${item.is_available ? 'text-green-600' : 'text-gray-400'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleItemDelete(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-gray-500">Organize your menu with categories</p>
            {canManage && (
              <Button onClick={openAddCategoryDialog} className="bg-primary hover:bg-primary/90" data-testid="add-category-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            )}
          </div>

          {categories.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Tag className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">No Categories</h3>
                <p className="text-gray-400 mb-4">Create categories to organize your menu</p>
                {canManage && (
                  <Button onClick={openAddCategoryDialog} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => {
                const itemCount = items.filter(i => i.category_id === category.id).length;
                return (
                  <Card key={category.id} data-testid={`category-${category.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-800">{category.name}</h3>
                          <p className="text-sm text-gray-500">{itemCount} items</p>
                        </div>
                        <Badge variant="outline">Order: {category.display_order}</Badge>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-500 mb-3">{category.description}</p>
                      )}
                      {canManage && (
                        <div className="flex gap-2 pt-2 border-t">
                          <Button variant="ghost" size="sm" onClick={() => openEditCategoryDialog(category)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600"
                            onClick={() => handleCategoryDelete(category.id)}
                            disabled={itemCount > 0}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Item Dialog */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="max-w-md" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleItemSubmit} className="space-y-4">
            <div>
              <Label htmlFor="item_name">Item Name *</Label>
              <Input
                id="item_name"
                value={itemFormData.name}
                onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                placeholder="e.g., Butter Chicken"
                data-testid="item-name-input"
              />
            </div>
            <div>
              <Label htmlFor="item_category">Category</Label>
              <Select
                value={itemFormData.category_id}
                onValueChange={(value) => setItemFormData({ ...itemFormData, category_id: value })}
              >
                <SelectTrigger data-testid="item-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item_price">Price (₹) *</Label>
              <Input
                id="item_price"
                type="number"
                step="0.01"
                min="0"
                value={itemFormData.price}
                onChange={(e) => setItemFormData({ ...itemFormData, price: e.target.value })}
                placeholder="299"
                data-testid="item-price-input"
              />
            </div>
            <div>
              <Label htmlFor="item_description">Description</Label>
              <Textarea
                id="item_description"
                value={itemFormData.description}
                onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                data-testid="item-description-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setItemDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-item-btn">
                {editingItem ? 'Update' : 'Create'} Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label htmlFor="cat_name">Category Name *</Label>
              <Input
                id="cat_name"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                placeholder="e.g., Main Course"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="cat_description">Description</Label>
              <Textarea
                id="cat_description"
                value={categoryFormData.description}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
                data-testid="category-description-input"
              />
            </div>
            <div>
              <Label htmlFor="cat_order">Display Order</Label>
              <Input
                id="cat_order"
                type="number"
                min="0"
                value={categoryFormData.display_order}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, display_order: e.target.value })}
                data-testid="category-order-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-category-btn">
                {editingCategory ? 'Update' : 'Create'} Category
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
